// ── ADMIN: BULK CONTRACT ACTIONS (returning families) ──────────────────
// Returning families re-enroll and pay during the priority window; their
// contracts are sent later, in bulk, once the season template is finalized.
// esc (shared classic script) stays a bare global — not migrated yet.
// sendContract/remindContract/declineApprovedContract/downloadContract are
// real exports, called via onclick="" from admin-registrations.js
// (renderPipeline) and admin-accounts.js — wired via each consumer's own
// VIEW_INIT instead of window/onclick. confirmSendContract/
// confirmRemindContract/confirmDeclineApprovedContract stay file-local —
// only reached from this file's own openModal() output, attached directly
// inline the same way billing-shared.js's cardModal attaches its Save
// button.
import { DB, PROGRAMS } from '../core/state.js';
import { apiFetch } from '../core/api.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { refresh, refreshAdminData } from '../core/routing.js';

// Resolve a family contract target from a familyKey ('acct:<id>' returning, 'reg:<id>' new).
function resolveContractTarget(familyKey, programId) {
  const upSeason = DB.settings.upcomingSeasonYear;
  if (familyKey.startsWith('acct:')) {
    const accountId = familyKey.slice(5);
    const acct = DB.users.find(u => u.id === accountId);
    const ses = DB.seasonEnrollments.filter(se =>
      se.seasonYear === upSeason && se.programId === programId &&
      se.status === 'registered' && (() => { const c = DB.children.find(x => x.id === se.performerId); return c && c.accountId === accountId; })());
    return { kind: 'returning', accountId, email: acct ? acct.email : '', familyName: acct ? `${acct.firstName} ${acct.lastName}` : 'family', ses };
  }
  const regId = familyKey.slice(4);
  const r = DB.registrations.find(x => x.id === regId);
  if (!r) return null;
  const contract = (r.contracts || []).find(c => c.programId === programId);
  return { kind: 'new', reg: r, email: r.parent.email, familyName: `${r.parent.firstName} ${r.parent.lastName}`, contract };
}

export function sendContract(familyKey, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  if (!DB.settings.firmaTemplates[programId]) {
    openModal(`
      <h3>Template not set</h3>
      <p class="card-intro">Set the ${esc(p.name)} Firma template ID in Settings before sending contracts.</p>
      <button class="btn" style="width:auto" onclick="closeModal();route('a-settings')">Go to Settings</button>`, true);
    return;
  }
  const t = resolveContractTarget(familyKey, programId);
  if (!t) return;
  openModal(`
    <h3>Send ${esc(p.name)} contract</h3>
    <p class="card-intro">Send the ${esc(p.name)} contract to <b style="color:var(--cream)">${esc(t.familyName)}</b> at ${esc(t.email)}? They'll receive their contract to sign.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="sendContractBtn" style="flex:1">Send contract</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('sendContractBtn').addEventListener('click', () => confirmSendContract(familyKey, programId));
}

async function confirmSendContract(familyKey, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  const t = resolveContractTarget(familyKey, programId);
  if (!t) { closeModal(); return; }
  const btn = document.querySelector('#modal .btn:not(.btn-ghost)');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Sending…'; }
  try {
    const payload = { programId };
    if (t.kind === 'returning') payload.accountId = t.accountId;
    else payload.registrationId = familyKey.slice(4);
    const result = await apiFetch('/contracts/send', { method: 'POST', body: JSON.stringify(payload) });
    closeModal(); await refreshAdminData(true);
    toast(result.message || `${p.name} contract sent to ${t.familyName}`);
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Send contract'; }
    toastError(e.message);
  }
}

export async function downloadContract(contractId) {
  try {
    const result = await apiFetch('/contracts/' + contractId + '/download');
    if (result.downloadUrl) window.open(result.downloadUrl, '_blank', 'noopener');
  } catch(e) { toastError(e.message); }
}

export function remindContract(familyKey, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  const t = resolveContractTarget(familyKey, programId);
  if (!t) return;
  openModal(`
    <h3>Send ${esc(p.name)} reminder</h3>
    <p class="card-intro">Send <b style="color:var(--cream)">${esc(t.familyName)}</b> a reminder to sign their ${esc(p.name)} contract? This nudges their existing contract — no new contract is sent.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="remindContractBtn" style="flex:1">Send reminder</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('remindContractBtn').addEventListener('click', () => confirmRemindContract(familyKey, programId));
}

async function confirmRemindContract(familyKey, programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  const t = resolveContractTarget(familyKey, programId);
  if (!t) { closeModal(); return; }
  // Find the contract ID from the local data
  const contracts = await apiFetch('/contracts?programId=' + programId + '&status=pending' + (t.kind === 'returning' ? '&accountId=' + t.accountId : '&registrationId=' + familyKey.slice(4)));
  if (!contracts.length) { toast('No pending contract found'); closeModal(); return; }
  try {
    const result = await apiFetch('/contracts/' + contracts[0].id + '/remind', { method: 'POST' });
    closeModal(); await refreshAdminData(true);
    toast(result.message || `Reminder sent to ${t.familyName}`);
  } catch (e) { toastError(e.message); }
}

// Decline an already-approved family that was sent a contract but never signed
// and/or paid in time -- new-family registrations only (see the r.source==='New'
// gate where this is called from in renderPipeline()). Operates on every still-
// approved performer tied to this family+program contract, same granularity as
// sendContract/remindContract above (one contract can cover multiple siblings).
export function declineApprovedContract(familyKey, programId) {
  const t = resolveContractTarget(familyKey, programId);
  if (!t || t.kind !== 'new') return;
  const prog = PROGRAMS.find(p => p.id === programId);
  const affected = t.reg.children.filter(c => c.programInterest === programId && c.status === 'approved');
  if (!affected.length) return;
  const names = affected.map(c => `${c.firstName} ${c.lastName}`).join(', ');
  openModal(`
    <h3>Decline ${esc(prog ? prog.name : 'program')} contract</h3>
    <p class="card-intro">You're about to decline ${affected.length > 1 ? 'these performers' : 'this performer'}. ${esc(t.familyName)} will receive an email letting them know${t.contract && t.contract.sent ? ', and their contract signing request will be cancelled' : ''}.</p>
    <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:14px 16px;margin-bottom:18px;font-size:13px;line-height:1.9">
      <div><span style="color:var(--muted)">Parent:</span> <span style="color:var(--cream)">${esc(t.familyName)}</span></div>
      <div><span style="color:var(--muted)">Performer${affected.length > 1 ? 's' : ''}:</span> <span style="color:var(--cream)">${esc(names)}</span></div>
      <div><span style="color:var(--muted)">Program:</span> <span style="color:var(--cream)">${esc(prog ? prog.name : programId)}</span></div>
    </div>
    <div style="display:flex;gap:10px">
      <button class="btn" id="declineContractBtn" style="flex:1;background:#f87171">Decline contract</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('declineContractBtn').addEventListener('click', () => confirmDeclineApprovedContract(familyKey, programId));
}

async function confirmDeclineApprovedContract(familyKey, programId) {
  const t = resolveContractTarget(familyKey, programId);
  if (!t || t.kind !== 'new') { closeModal(); return; }
  const btn = document.querySelector('#modal .btn:not(.btn-ghost)');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Declining…'; }
  try {
    const result = await apiFetch('/contracts/decline', {
      method: 'POST', body: JSON.stringify({ registrationId: familyKey.slice(4), programId })
    });
    t.reg.children.forEach(c => { if (c.programInterest === programId && c.status === 'approved') c.status = 'declined'; });
    t.reg.contracts = (t.reg.contracts || []).filter(c => c.programId !== programId);
    closeModal();
    refresh();
    if (result.warning) toast(result.warning, 'error');
    toast(result.message || 'Declined');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Decline contract'; }
    toastError(e.message);
  }
}
